import { State } from '@progress/kendo-data-query';
import { useCallback, useEffect, useState } from 'react';
import Image from 'src/components/Image';
import PreviewPDF from 'src/components/PreviewPDF';
import Checkbox from 'src/components/design-system/Checkbox';
import useEnterInvoices from 'src/hooks/useEnterInvoices';
import useWindowSize from 'src/hooks/useWindowSize';
import useAttachDocumentApi from 'src/services/useAttachDocumentApi';
import { IAttachDocument } from 'src/types/AttachDocument';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { validSignedUrl } from 'src/utils/strings';

export const initialDataState = (attachId: string): State => ({
  skip: 0,
  take: 0,
  sort: [],
  filter: {
    logic: 'and',
    filters: [
      {
        logic: 'and',
        filters: [
          {
            field: 'attachId',
            value: attachId,
            operator: 'eq',
          },
        ],
      },
    ],
  },
});

const dateArr = ['creationDate'];

function OcrInvoicePreviewSection() {
  const { height, width } = useWindowSize();
  const { attachId, selectedCompany } = useEnterInvoices();
  const { attachDocument, isAttachDocumentLoading } = useAttachDocumentApi(true);

  const [document, setDocument] = useState<IAttachDocument>({} as IAttachDocument);
  const [totalDocuments, setTotalDocuments] = useState(1);
  const fetchOcrDocument = useCallback(async () => {
    const requestBody = modifyRequest(initialDataState(attachId), dateArr);
    if (selectedCompany.companyId) {
      attachDocument({ companyId: selectedCompany.companyId, body: requestBody }).then((res) => {
        if (res?.items) {
          setDocument(res.items[0]);
          setTotalDocuments(res.items.length);
        }
      });
    }
  }, [attachDocument, attachId, selectedCompany.companyId]);
  useEffect(() => {
    fetchOcrDocument();
  }, [fetchOcrDocument]);

  const isPDF = document?.description?.toLowerCase().endsWith('.pdf');
  return (
    <>
      {isAttachDocumentLoading ? (
        <div className="w-full h-[calc(100vh-100px)] flex items-center justify-center">
          <span className="k-icon k-i-loading text-xl" />
        </div>
      ) : (
        <>
          <div
            className="flex justify-center"
            data-tooltip-id={'global-tooltip'}
            data-tooltip-content={
              'The document values are auto-populated from this scan; therefore, it must be completed with this scan attached.'
            }
          >
            <Checkbox label="Attach to invoice" checked={true} disabled={true} />
          </div>
          <div className="flex justify-center my-2">
            <h3 className="scan__doc__count">
              Document {totalDocuments > 0 ? 1 : 0} of {totalDocuments}
            </h3>
          </div>
          {!isPDF && (
            <div className="scan__imgContainer">
              <Image
                src={`${validSignedUrl(document?.signedUrl)}`}
                controls
                filename={document?.originalFile}
                styles={{
                  width: `${width}px`,
                  maxHeight: `${height - 210}px`,
                }}
              />
            </div>
          )}
          {isPDF && (
            <PreviewPDF
              url={`${validSignedUrl(document?.signedUrl)}`}
              height={`${height - 180}px`}
              filename={document?.originalFile}
            />
          )}
        </>
      )}
    </>
  );
}

export default OcrInvoicePreviewSection;
